import { Box, useTheme } from "@mui/material";
import { motion } from "framer-motion";

const MotionPath = motion.path;
const MotionRect = motion.rect;
const MotionCircle = motion.circle;

/**
 * Self-contained, theme-aware SVG of an analytics dashboard with an
 * animated chart, KPI tiles and a small "neural" node graph. Used as the
 * hero product visual — crisp at any resolution and zero network cost.
 */
export default function DashboardArt() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const panel = isDark ? "#0e1428" : "#ffffff";
  const panelAlt = isDark ? "#141c38" : "#f1f5ff";
  const stroke = isDark ? "rgba(148,163,184,0.18)" : "rgba(11,18,32,0.08)";
  const textBar = isDark ? "rgba(199,210,254,0.35)" : "rgba(11,18,32,0.18)";

  return (
    <Box sx={{ width: "100%", filter: "drop-shadow(0 30px 60px rgba(13,18,40,0.35))" }}>
      <svg viewBox="0 0 520 380" width="100%" role="img" aria-label="Product analytics dashboard">
        <defs>
          <linearGradient id="da-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4f7cff" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#4f7cff" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="da-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
          <linearGradient id="da-bar" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#4f7cff" />
            <stop offset="100%" stopColor="#22d3ee" />
          </linearGradient>
        </defs>

        {/* Main window */}
        <rect x="8" y="8" width="504" height="364" rx="22" fill={panel} stroke={stroke} />
        <circle cx="34" cy="34" r="5" fill="#ff5f57" />
        <circle cx="52" cy="34" r="5" fill="#febc2e" />
        <circle cx="70" cy="34" r="5" fill="#28c840" />
        <rect x="150" y="28" width="220" height="12" rx="6" fill={panelAlt} />

        {/* KPI tiles */}
        {[0, 1, 2].map((i) => (
          <g key={i}>
            <rect x={28 + i * 116} y={60} width="104" height="64" rx="14" fill={panelAlt} stroke={stroke} />
            <rect x={42 + i * 116} y={74} width="40" height="8" rx="4" fill={textBar} />
            <MotionRect
              x={42 + i * 116}
              y={92}
              height="14"
              rx="4"
              fill="url(#da-bar)"
              initial={{ width: 0 }}
              animate={{ width: [0, 60 - i * 8] }}
              transition={{ duration: 1.2, delay: 0.3 + i * 0.15, ease: "easeOut" }}
            />
          </g>
        ))}

        {/* Big area chart */}
        <rect x="28" y="140" width="320" height="210" rx="16" fill={panelAlt} stroke={stroke} />
        <MotionPath
          d="M48 300 L100 260 L150 280 L200 215 L255 240 L312 175 L312 330 L48 330 Z"
          fill="url(#da-area)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
        />
        <MotionPath
          d="M48 300 L100 260 L150 280 L200 215 L255 240 L312 175"
          fill="none"
          stroke="url(#da-line)"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.6, delay: 0.4, ease: "easeInOut" }}
        />
        {[[100, 260], [200, 215], [312, 175]].map(([cx, cy], i) => (
          <MotionCircle
            key={i}
            cx={cx}
            cy={cy}
            r="4.5"
            fill="#fff"
            stroke="#a855f7"
            strokeWidth="3"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.4, delay: 1.4 + i * 0.15 }}
          />
        ))}

        {/* Side bar chart */}
        <rect x="362" y="140" width="122" height="100" rx="16" fill={panelAlt} stroke={stroke} />
        {[28, 52, 40, 64].map((h, i) => (
          <MotionRect
            key={i}
            x={378 + i * 24}
            width="14"
            rx="4"
            fill="url(#da-bar)"
            initial={{ height: 0, y: 220 }}
            animate={{ height: h, y: 220 - h }}
            transition={{ duration: 0.8, delay: 0.8 + i * 0.12, ease: "easeOut" }}
          />
        ))}

        {/* Neural node graph */}
        <rect x="362" y="256" width="122" height="94" rx="16" fill={panelAlt} stroke={stroke} />
        {(() => {
          const nodes = [
            [388, 280], [388, 326], [423, 303], [458, 280], [458, 326],
          ];
          const edges = [[0, 2], [1, 2], [2, 3], [2, 4]];
          return (
            <g>
              {edges.map(([a, b], i) => (
                <MotionPath
                  key={`e${i}`}
                  d={`M${nodes[a][0]} ${nodes[a][1]} L${nodes[b][0]} ${nodes[b][1]}`}
                  stroke="url(#da-line)"
                  strokeWidth="2"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 0.8 }}
                  transition={{ duration: 0.8, delay: 1.2 + i * 0.1 }}
                />
              ))}
              {nodes.map(([cx, cy], i) => (
                <MotionCircle
                  key={`n${i}`}
                  cx={cx}
                  cy={cy}
                  r="6"
                  fill={i === 2 ? "#a855f7" : "#4f7cff"}
                  initial={{ scale: 0 }}
                  animate={{ scale: [0, 1.2, 1] }}
                  transition={{ duration: 0.5, delay: 1 + i * 0.08 }}
                />
              ))}
            </g>
          );
        })()}
      </svg>
    </Box>
  );
}
